import React from 'react';
import { withSpecificationLine } from './LineSpecifications';
import dayjs from 'dayjs';
import type { PurchaseReturnDTO } from '@/services/purchaseReturn.service';
import { headerProjectLabel, projectLabel } from '@/utils/projectDisplay';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';

export interface PrintablePurchaseReturnProps {
  purchaseReturn: PurchaseReturnDTO;
  onClose?: () => void;
}

function amt(v: unknown): string {
  const n = Number(v ?? 0);
  return (Number.isNaN(n) ? 0 : n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Purchase return — shared pagination engine with restored columns. */
export const PrintablePurchaseReturn: React.FC<PrintablePurchaseReturnProps> = ({
  purchaseReturn,
  onClose,
}) => {
  const lines = purchaseReturn.lines ?? [];
  const projectHeader = headerProjectLabel(purchaseReturn as any, lines as any);
  const supplierName =
    purchaseReturn.supplier?.supplier_name ?? String(purchaseReturn.supplier_id ?? '');

  return (
    <EngineCommercialA4Print
      docType="purchase_return"
      title="PURCHASE RETURN"
      documentNumber={purchaseReturn.return_number || 'PRTN'}
      partyName={supplierName}
      onClose={onClose}
      metaLines={[
        { label: 'Return No', value: purchaseReturn.return_number || '' },
        {
          label: 'Date',
          value: purchaseReturn.return_date
            ? dayjs(purchaseReturn.return_date).format('DD MMM YYYY')
            : '',
        },
        { label: 'Status', value: purchaseReturn.status || '' },
        { label: 'Project', value: projectHeader !== '-' ? projectHeader : '' },
        { label: 'Supplier', value: supplierName },
        {
          label: 'GRN',
          value:
            purchaseReturn.goodsReceiptNote?.grn_number ??
            String(purchaseReturn.grn_id ?? ''),
        },
        { label: 'Reason', value: purchaseReturn.return_reason || '' },
        { label: 'Remarks', value: purchaseReturn.remarks || '' },
      ].filter((m) => m.value)}
      columns={[
        { key: 'sno', label: '#' },
        { key: 'code', label: 'Item' },
        { key: 'description', label: 'Description' },
        { key: 'project', label: 'Project' },
        { key: 'qty', label: 'Return Qty', align: 'right' },
        { key: 'uom', label: 'UOM' },
        { key: 'rate', label: 'Unit rate', align: 'right' },
        { key: 'amount', label: 'Amount', align: 'right' },
      ]}
      lines={lines.map((line) => ({
        lineNumber: line.line_number,
        description: withSpecificationLine(line.supplier_description || line.item_description || '—', line.specifications),
        cells: {
          code: line.item_code || '—',
          project: projectLabel(line as any),
          qty: Number(line.return_quantity).toLocaleString(),
          uom: line.uom || '—',
          rate: amt(line.unit_rate),
          amount: amt(line.amount),
        },
      }))}
    />
  );
};
